/**
 * Province / regional subdivision configuration per country.
 *
 * Background: the original form treated "Provincia" as a hard
 * 2-letter ISO 3166-2:IT code (e.g. MI / RM / PN). That logic is
 * correct ONLY for Italy and Switzerland (Cantons also use 2-letter
 * codes). Every other supported EU country uses a free-text regional
 * subdivision name (Bundesland, Województwo, Département, etc.).
 *
 * This module returns the appropriate field configuration so the
 * customer wallet form can:
 *   • show the LOCAL official terminology in the label
 *   • use a representative placeholder
 *   • enforce 2-letter uppercase only for IT / CH
 *   • allow up to 50 characters of free text everywhere else
 *
 * Fabrizio request 03/06/2026 (Option A — country-aware validation).
 */

// Two-letter strict territories (sigla automobilistica / Cantons).
const STRICT_2_LETTER = new Set(['IT', 'CH']);

// Default labels in 6 most-used UI languages so the field "speaks"
// the native administrative terminology (e.g. "Bundesland" reads
// natural to a German customer). Fallback chain: countryCode →
// language-bound default → "Province".
const PROVINCE_CONFIG_BY_COUNTRY = {
  IT: { en: 'Province', it: 'Provincia', placeholder: 'RM' },
  CH: { en: 'Canton', it: 'Cantone', de: 'Kanton', fr: 'Canton', placeholder: 'ZH' },
  DE: { en: 'State (Bundesland)', it: 'Land', de: 'Bundesland', placeholder: 'Bayern' },
  AT: { en: 'State (Bundesland)', it: 'Land', de: 'Bundesland', placeholder: 'Wien' },
  FR: { en: 'Département', it: 'Dipartimento', fr: 'Département', placeholder: '75' },
  BE: { en: 'Province', it: 'Provincia', fr: 'Province', nl: 'Provincie', placeholder: 'Anvers' },
  LU: { en: 'District', it: 'Distretto', fr: 'District', de: 'Bezirk', placeholder: 'Luxembourg' },
  NL: { en: 'Provincie', it: 'Provincia', nl: 'Provincie', placeholder: 'Noord-Holland' },
  ES: { en: 'Province', it: 'Provincia', es: 'Provincia', placeholder: 'Madrid' },
  PT: { en: 'District', it: 'Distretto', pt: 'Distrito', placeholder: 'Lisboa' },
  PL: { en: 'Voivodeship', it: 'Voivodato', pl: 'Województwo', placeholder: 'mazowieckie' },
  SE: { en: 'County', it: 'Contea', sv: 'Län', placeholder: 'Stockholm' },
  NO: { en: 'County', it: 'Contea', no: 'Fylke', placeholder: 'Oslo' },
  FI: { en: 'Region', it: 'Regione', fi: 'Maakunta', placeholder: 'Uusimaa' },
  DK: { en: 'Region', it: 'Regione', da: 'Region', placeholder: 'Hovedstaden' },
  CZ: { en: 'Region', it: 'Regione', cs: 'Kraj', placeholder: 'Praha' },
  GR: { en: 'Region', it: 'Regione', el: 'Periferia', placeholder: 'Attiki' },
  HU: { en: 'County', it: 'Contea', hu: 'Megye', placeholder: 'Budapest' },
  RO: { en: 'County', it: 'Contea', ro: 'Judet', placeholder: 'Bucuresti' },
  IRL: { en: 'County', it: 'Contea', placeholder: 'Dublin' },
  GBZ: { en: 'County', it: 'Contea', placeholder: 'London' },
  GB: { en: 'County', it: 'Contea', placeholder: 'London' },
};

/**
 * Return the field configuration for the given country (and UI lang).
 * Always returns a usable object — falls back to a generic "Province"
 * config when the country isn't in our table.
 */
export function getProvinceConfig(countryCode, uiLang = 'en') {
  const code = (countryCode || 'IT').toUpperCase();
  const lang = (uiLang || 'en').toLowerCase();
  const entry = PROVINCE_CONFIG_BY_COUNTRY[code] || {};
  // Prefer label in the customer's UI language, then English, then "Province".
  const label = entry[lang] || entry.en || 'Province';
  const placeholder = entry.placeholder || '';
  const isStrict = STRICT_2_LETTER.has(code);
  return {
    label,
    placeholder,
    maxLength: isStrict ? 2 : 50,
    autoUppercase: isStrict,
    pattern: isStrict ? '^[A-Z]{2}$' : null,
    helperText: isStrict
      ? 'Sigla 2 lettere maiuscole'
      : '',
  };
}
